import { autoinject } from 'aurelia-framework';

import { FormWithTableApi } from '~/api/form-with-table-api';
import { APIParameters, GenericApi } from '~/api/generic-api';
import { AppRouteID } from '~/app-routes';
import { CodeAndDescription } from '~/models/code-and-description';
import { ConfigKeyValue } from '~/models/config-key-value';
import { FormModel, isFormModel } from '~/models/form-model';
import { InsertResult, isInsertResult } from '~/models/insert-result';
import {
	InsertUpdateResult,
	isInsertUpdateResult,
} from '~/models/insert-update-result';
import { ValueMap } from '~/models/value-map';
import { FetchUtils, HttpMethod } from '~/utils/fetch-utils';
import { Logger } from '~/utils/logger';

export interface UpdatedCodeListResponse {
	codeAndDescriptionKey: string | null;
	codeList: CodeAndDescription[];
}

@autoinject
export class ClassicFormsApi extends FormWithTableApi {
	// calculate the api by version
	public setCurrentApiVersion(apiVersion?: string) {
		if (apiVersion) {
			this.baseApiUrl += apiVersion + '/';
		} else {
			this.baseApiUrl = 'api/';
		}
	}

	public getClassicFormData(
		functionName: string,
		recordId: number | string,
		params?: APIParameters,
	): Promise<FormModel> {
		// NOTE(Lucas): 2020-07-07
		// We must use this to support "restore-batch-api" overriding this
		// behaviour for now, until we think of a better solution
		const relativeUri = this.computeClassicFormDataURL(
			functionName,
			recordId,
		);
		return FetchUtils.getJSON(relativeUri, {
			'field-model': true,
			...params,
		}).then(GenericApi.translateFormModelFormat);
	}

	public getPreInsertClassicForm(
		functionName: string,
		params?: APIParameters,
	): Promise<any> {
		if (!functionName) {
			throw new Error(
				'getPreInsertClassicForm: Cannot use empty functionName string',
			);
		}
		return FetchUtils.getJSON(
			this.baseApiUrl + ':functionName/pre-insert',
			{
				functionName,
				'field-model': true,
				...params,
			},
		).then(GenericApi.translateFormModelFormat);
	}

	public async getConfigFlag(flagNo: number): Promise<boolean> {
		const r = await FetchUtils.getJSON(
			this.baseApiUrl + 'config-items/:div/:flag',
			{
				div: GenericApi.getDivision(),
				flag: flagNo,
			},
		);
		if (r !== true && r !== false) {
			throw new Error(
				'Config items endpoint did not return a boolean as expected.',
			);
		}
		return r;
	}

	public async getConfigItem(): Promise<any> {
		const response = await FetchUtils.getJSON(
			'api/' + 'config-items/:div',
			{
				div: GenericApi.getDivision(),
			},
		);
		return response;
	}

	public getCustomData(functionName: string, endpoint: string): Promise<any> {
		return FetchUtils.getJSON(this.baseApiUrl + ':functionName/:endpoint', {
			functionName,
			endpoint,
		});
	}

	public async saveClassicFormData<
		FormModelFieldNames extends string = string,
	>(
		functionName: string,
		formModel: FormModel<FormModelFieldNames>,
		customApi?: string,
		formExtension?: string,
		recordId?: number,
		customHttpMethod?: HttpMethod,
		// NOTE(Jae): 2020-04-27 - WEBUI-3346
		// queryParams are special additional parameters added to the request.
		// such as the Pension Summary Reversionary "Add" dialog, which
		queryParams?: Map<string, string>,
	): Promise<
		| FormModel<FormModelFieldNames>
		| InsertResult
		| InsertUpdateResult
		| undefined
	> {
		let httpMethod: HttpMethod = HttpMethod.POST;
		if (customHttpMethod) {
			httpMethod = customHttpMethod;
		} else if (recordId) {
			httpMethod = HttpMethod.PUT;
		}

		// Compute url
		let url = '';
		if (customApi) {
			if (formExtension) {
				url =
					this.baseApiUrl +
					functionName +
					'/' +
					formExtension +
					'/' +
					customApi;
			} else {
				url = this.baseApiUrl + functionName + '/' + customApi;
			}
		} else {
			// NOTE(Jae): 2020-03-04
			// We must use this to support "restore-batch-api" overriding this
			// behaviour for now, until we think of a better solution
			url = this.computeClassicFormDataURL(functionName, recordId);
		}

		// NOTE(Jae): 2020-04-27 - WEBUI-3346
		// A hatch used by various forms to add a bit of extra data to a request.
		// Used by "Add Reversionary" dialog on Pension Summary
		const params = {
			'field-model': true,
		};
		if (queryParams) {
			queryParams.forEach((value, key) => {
				params[key] = value;
			});
		}

		// Get result
		let body: FormModel | InsertResult | InsertUpdateResult;
		try {
			body = await FetchUtils.fetchJSON<
				FormModel | InsertResult | InsertUpdateResult,
				ValueMap
			>(
				httpMethod,
				url,
				params,
				GenericApi.translateFormModelToValueMap(formModel),
			);
		} catch (e) {
			throw e;
		}
		if (!body) {
			return undefined;
		}
		try {
			return ClassicFormsApi.translateResponseBodyFormat(body);
		} catch (e) {
			const errorMessage = FetchUtils.normalizeError(e);
			const message = this.i18n.tr(
				'errors.unexpected-body-for-save-api-response',
				{
					message: errorMessage,
				},
			);
			Logger.error(message);
			throw new Error(message);
		}
	}

	/**
	 * computeClassicFormDataURL can be overriden by subclasses to change how this url is created
	 */
	protected computeClassicFormDataURL(
		functionName: string,
		recordId?: number | string,
	): string {
		let url = this.baseApiUrl + functionName;
		if (recordId) {
			url += '/' + recordId;
		}
		return url;
	}

	public getCSVData(
		functionName: string,
		numberOfRecords: number,
		tableParams: APIParameters,
		queryParams?: Map<string, string>,
	): Promise<any> {
		// Convert Map to JS Object
		const queryParamsAsObj: { [fieldName: string]: string } = {};
		queryParams.forEach((value, key) => {
			queryParamsAsObj[key] = value;
		});
		return FetchUtils.getJSON(
			this.baseApiUrl + ':functionName/csv-data/:limit',
			{
				...tableParams,
				...queryParamsAsObj,
				functionName,
				limit: numberOfRecords,
			},
		);
	}

	/**
	 * getEndFieldFormData is for processing endFields, which are events that replace the formModel when
	 * certain fields are focused and then unfocused. This was implemented so WebUI (this project) works like TreeView.
	 *
	 * How to test:
	 * - Go to: http://localhost:9000/#/family-law-split-details?division=5&nav-id=MEMBER&nav-view=e2e-navigation01
	 * - Tab/Click on the field "Family Law Status" (PGz_Status)
	 * - Tab off to fire this event or unfocus the field
	 */
	public getEndFieldFormData(
		functionName: string,
		formModel: FormModel,
		fieldName: string,
		queryParams?: Map<string, string>,
	): Promise<FormModel> {
		const queryParamsAsObj: { [fieldName: string]: string } = {};
		if (queryParams) {
			queryParams.forEach((value, key) => {
				queryParamsAsObj[key] = value;
			});
		}
		return FetchUtils.fetchJSON(
			HttpMethod.PUT,
			this.baseApiUrl + ':functionName/:fieldName/end-field',
			{
				functionName,
				fieldName,
				'field-model': true,
				...queryParamsAsObj,
			},
			GenericApi.translateFormModelToClassicFormStateModel(formModel),
		).then(GenericApi.translateFormModelFormat);
	}

	public deleteRecord(
		recordId: number,
		functionName: string,
		deleteOrUndelete: boolean,
		queryParams?: Map<string, string>,
	): Promise<any> {
		const queryParamsAsObj: { [fieldName: string]: string } = {};
		if (queryParams) {
			queryParams.forEach((value, key) => {
				queryParamsAsObj[key] = value;
			});
		}
		return FetchUtils.fetchJSON(
			HttpMethod.DELETE,
			this.baseApiUrl + ':functionName/:recordId/:deleteOrUndelete',
			{
				functionName,
				recordId,
				deleteOrUndelete,
				'field-model': true,
				...queryParamsAsObj,
			},
		).then(GenericApi.translateFormModelFormat);
	}

	/**
	 * Checks that the provided subtype is valid for this function.
	 * We expect the response to be a boolean. Throw an error if it is not.
	 */
	public async isValidSubtype(functionName: string): Promise<boolean> {
		const isValidSubtype = await FetchUtils.getJSON(
			this.baseApiUrl + ':functionName/valid-subtype',
			{
				functionName,
			},
		);
		if (isValidSubtype !== true && isValidSubtype !== false) {
			throw new Error(
				'Valid Subtype endpoint did not return a boolean as expected.',
			);
		}
		return isValidSubtype;
	}

	public getConfigValue(key: string): Promise<ConfigKeyValue> {
		return this.getCustomData('config-file', key);
	}

	/**
	 * We expect the response to only be one of three objects. If it is not, throw an error
	 */
	private static translateResponseBodyFormat(
		responseBody: any,
	): FormModel | InsertResult | InsertUpdateResult {
		let responseObject: FormModel;
		if (
			isInsertResult(responseBody) ||
			isInsertUpdateResult(responseBody)
		) {
			return responseBody;
		} else {
			responseObject = GenericApi.translateFormModelFormat(responseBody);
		}
		if (!isFormModel(responseObject)) {
			throw new Error(
				'Unexpected response body format. Missing "fields" property.',
			);
		}
		return responseObject;
	}

	public getLatestRetentionProcessingInProgress(
		processType: string,
	): Promise<FormModel> {
		return FetchUtils.getJSON<FormModel>(
			this.baseApiUrl +
				AppRouteID.DataRetentionProcessing +
				'/in-progress',
			{
				processType,
				'field-model': true,
			},
		).then(GenericApi.translateFormModelFormat);
	}
}
